import { IStory } from '../../../types/stories'

/**
 * What the viewer remembers per browser: where each story stopped (clip and
 * second), which clips were opened, and the sound choice. One key for every page
 * and every Stories block, so the home page and the show page agree.
 */
export const STORIES_STORAGE_KEY = 'r13.stories.v1'
const STATE_VERSION = 1
/** the PRD asks for a year */
export const STORY_PROGRESS_TTL_MS = 365 * 24 * 60 * 60 * 1000
/** oldest entries go first above this (a season is ~20 stories) */
export const MAX_STORED_STORIES = 300
/** a stop this close to the clip's end resumes from the clip's start */
const RESUME_END_GUARD_S = 1

export interface IStoryProgress {
  /** clip to resume on, 0-based */
  c: number
  /** seconds into that clip */
  t: number
  /** clips opened at least once, 0-based */
  v: number[]
  /** last change, epoch ms */
  u: number
}

export interface IStoriesState {
  v: typeof STATE_VERSION
  /** the user's sound choice; unset = never chosen */
  muted?: boolean
  s: Record<string, IStoryProgress>
}

export type StoryStatus = 'new' | 'unwatched' | 'progress' | 'watched'

export const emptyStoriesState = (): IStoriesState => ({
  v: STATE_VERSION,
  s: {},
})

const isIndex = (n: unknown): n is number =>
  typeof n === 'number' && Number.isInteger(n) && n >= 0

const toProgress = (raw: unknown): IStoryProgress | null => {
  const p = raw as Partial<IStoryProgress> | null
  if (!p || !isIndex(p.c) || typeof p.u !== 'number' || !Array.isArray(p.v)) {
    return null
  }
  const t = typeof p.t === 'number' && Number.isFinite(p.t) && p.t > 0 ? p.t : 0
  return { c: p.c, t, v: p.v.filter(isIndex), u: p.u }
}

/** drops expired entries and keeps the newest MAX_STORED_STORIES */
const prune = (s: Record<string, IStoryProgress>, now: number) => {
  const kept = Object.entries(s)
    .filter(([, p]) => now - p.u < STORY_PROGRESS_TTL_MS)
    .sort(([, a], [, b]) => b.u - a.u)
    .slice(0, MAX_STORED_STORIES)
  return Object.fromEntries(kept)
}

export const parseStoriesState = (
  raw: string | null,
  now: number
): IStoriesState => {
  if (!raw) {
    return emptyStoriesState()
  }
  try {
    const data = JSON.parse(raw) as Partial<IStoriesState>
    if (!data || data.v !== STATE_VERSION || typeof data.s !== 'object') {
      return emptyStoriesState()
    }
    const s: Record<string, IStoryProgress> = {}
    Object.entries(data.s ?? {}).forEach(([id, value]) => {
      const p = toProgress(value)
      if (p) {
        s[id] = p
      }
    })
    const state: IStoriesState = { v: STATE_VERSION, s: prune(s, now) }
    if (typeof data.muted === 'boolean') {
      state.muted = data.muted
    }
    return state
  } catch {
    return emptyStoriesState()
  }
}

/** Safari private mode and blocked storage throw: the viewer then just forgets */
export const readStoriesState = (now: number): IStoriesState => {
  try {
    return parseStoriesState(
      window.localStorage.getItem(STORIES_STORAGE_KEY),
      now
    )
  } catch {
    return emptyStoriesState()
  }
}

/** false when the state could not be stored */
export const writeStoriesState = (state: IStoriesState, now: number) => {
  try {
    window.localStorage.setItem(
      STORIES_STORAGE_KEY,
      JSON.stringify({ ...state, s: prune(state.s, now) })
    )
    return true
  } catch {
    // quota or blocked storage: keep the state for this visit only
    return false
  }
}

const update = (
  state: IStoriesState,
  id: string,
  change: (p: IStoryProgress) => IStoryProgress
): IStoriesState => {
  const current = state.s[id] ?? { c: 0, t: 0, v: [], u: 0 }
  return { ...state, s: { ...state.s, [id]: change(current) } }
}

/** the user is on `clip` at `time` (where the story resumes) */
export const withPosition = (
  state: IStoriesState,
  id: string,
  clip: number,
  time: number,
  now: number
) =>
  update(state, id, p => ({
    ...p,
    c: clip,
    t: time > 0 ? time : 0,
    u: now,
  }))

/**
 * The clip showed its first picture: the last clip's makes the story watched. A clip
 * that never played (it failed, it is blocked where the user is, the user
 * moved on first) does not
 */
export const withPlayed = (
  state: IStoriesState,
  id: string,
  clip: number,
  now: number
) =>
  update(state, id, p => ({
    ...p,
    v: p.v.includes(clip) ? p.v : [...p.v, clip].sort((a, b) => a - b),
    u: now,
  }))

/** the last clip played to its end: the next visit starts from the first clip */
export const withFinished = (state: IStoriesState, id: string, now: number) =>
  update(state, id, p => ({ ...p, c: 0, t: 0, u: now }))

export const withMuted = (state: IStoriesState, muted: boolean) => ({
  ...state,
  muted,
})

/**
 * watched = the story's last clip played (it need not have finished, and the
 * clips before it need not have played)
 */
export const isStoryWatched = (story: IStory, state: IStoriesState) => {
  const opened = state.s[story.id]?.v ?? []
  return story.cards.length > 0 && opened.includes(story.cards.length - 1)
}

/** the newest story (the first one sent) until the user opens it */
export const isStoryNew = (
  story: IStory,
  stories: IStory[],
  state: IStoriesState
) => stories[0]?.id === story.id && !state.s[story.id]

export const storyStatus = (
  story: IStory,
  stories: IStory[],
  state: IStoriesState
): StoryStatus => {
  if (isStoryWatched(story, state)) {
    return 'watched'
  }
  if (isStoryNew(story, stories, state)) {
    return 'new'
  }
  return state.s[story.id] ? 'progress' : 'unwatched'
}

/**
 * Right to left: the unwatched stories from the newest, then the watched ones
 * from the newest. The data comes newest first, so its order is kept.
 */
export const railOrder = (stories: IStory[], state: IStoriesState) => {
  const watched = (story: IStory) => isStoryWatched(story, state)
  return [
    ...stories.filter(story => !watched(story)),
    ...stories.filter(watched),
  ]
}

/**
 * The player's order, from the oldest to the newest (SHORTS-44): the data
 * comes newest first, so "next" is the story sent before this one and the
 * newest one ends the series. Watched stories keep their place. Temporary,
 * until the CMS toggle (RESH13-1293) says which way a block plays.
 */
export const seriesOrder = (stories: IStory[]) => [...stories].reverse()

/** where a story opens: the exact clip and second it stopped on */
export const resumePoint = (story: IStory, state: IStoriesState) => {
  const p = state.s[story.id]
  if (!p) {
    return { clip: 0, time: 0 }
  }
  const clip = Math.min(p.c, story.cards.length - 1)
  const duration = story.cards[clip]?.duration ?? 0
  const nearEnd = duration > 0 && p.t >= duration - RESUME_END_GUARD_S
  return { clip, time: nearEnd ? 0 : p.t }
}
